import {
  HEALTH_RESUME_STEP_FALLBACK_ORDER,
  HEALTH_RESUME_STEP_TARGETS,
} from "@/D2C/features/auth/config/healthResumeJourney.config";
import {
  LIFE_RESUME_STEP_FALLBACK_ORDER,
  LIFE_RESUME_STEP_TARGETS,
} from "@/D2C/features/auth/config/lifeResumeJourney.config";
import {
  MOTOR_RESUME_STEP_FALLBACK_ORDER,
  MOTOR_RESUME_STEP_TARGETS,
} from "@/D2C/features/auth/config/motorResumeJourney.config";
import {
  TRAVEL_RESUME_STEP_FALLBACK_ORDER,
  TRAVEL_RESUME_STEP_TARGETS,
} from "@/D2C/features/auth/config/travelResumeJourney.config";
import type {
  CarAdditionalInfoValues,
  CarDocumentUploadValues,
  CarDriverFormValues,
  CarPaymentValues,
  CarPersonalFormValues,
  CarQuoteSelectionValues,
  CarVehicleFormValues,
} from "@/D2C/features/car-quote/redux/type";
import type {
  HealthAdditionalInfoValues,
  HealthDetailsFormValues,
  HealthDocumentUploadValues,
  HealthHabitsFormValues,
  HealthPaymentValues,
  HealthPersonalFormValues,
  HealthQuickQuestionsFormValues,
  HealthQuoteSelectionValues,
} from "@/D2C/features/health-quote/redux/type";
import type { LifePlanDetailsFormValues } from "@/D2C/features/life-quote/redux/lifeQuoteSlice";
import type {
  TravelAdditionalInfoValues,
  TravelDetailsFormValues,
  TravelDocumentUploadValues,
  TravelPaymentValues,
  TravelPersonalFormValues,
  TravelQuoteSelectionValues,
} from "@/D2C/features/travel-quote/redux/type";
import { LOB_IDS, type LobId } from "@/shared/common/static";
import { LOCALISATION_CONFIG } from "@/shared/config/localisation";
import type { SharedPersonalDetailsState } from "@/shared/redux/reducers/personalDetailsSlice";
import {
  firstNonEmpty,
  isRecord,
  normalizeAmountValue,
  readRecordValue,
  toBoolean,
  toStringValue,
} from "@/shared/utils/hydrationValues";

type SessionPersonalDetails = {
  first_name?: string;
  last_name?: string;
  email?: string;
  email_id?: string;
  mobile_country_code?: string;
  mobile_number?: string;
  mobile_no?: string;
  partner_code?: string;
  friends_and_family_contact?: string;
  marketing_consent?: "yes" | "no";
};

type SessionVehicleDetails = {
  car_model?: string;
  model_year?: string | number;
  vehicle_variant?: string;
  gcc_spec?: boolean | string;
  is_new_car?: boolean | string;
  registered_emirate?: string;
  registration_date?: string;
  registered_under?: string;
  car_value?: string | number;
};

type SessionDriverDetails = {
  date_of_birth?: string;
  nationality?: string;
  gender?: string;
  uae_driving_experience?: string | number;
  outside_uae_driving_experience?: string | number;
  has_active_policy?: boolean | string;
  current_policy_type?: string;
  current_insurer?: string;
  claims_last_year?: boolean | string;
  no_claims_certificate?: string;
};

type SessionTravelDetails = {
  coverage_type?: string;
  departure?: string;
  destination?: string;
  plan_type?: "single" | "annual";
  cover_type?: string;
  travel_dates?: {
    start_date?: string;
    end_date?: string;
  };
  travellers?: Array<{
    first_name?: string;
    last_name?: string;
    name?: string;
    date_of_birth?: string;
  }>;
};

type SessionHealthDetails = {
  coverage_amount?: string | number;
  policy_type?: string;
  coverage_type?: "individual" | "family" | "family_floater" | string;
  start_date?: string;
  cover_end_date?: string;
  end_date?: string;
  policy_tenure?: string | number;
  current_insurer?: string;
  policy_expiry_date?: string;
  sum_insured?: string;
  claims_in_last_3_years?: boolean | string;
  policy_dates?: {
    start_date?: string;
    end_date?: string;
  };
  existing_policy_details?: {
    current_insurer?: string;
    policy_number?: string;
    policy_expiry_date?: string;
  };
  members?: Array<{
    name?: string;
    first_name?: string;
    last_name?: string;
    relationship?: string;
    date_of_birth?: string;
    dob?: string;
    pre_existing_condition?: boolean | string;
  }>;
};

type SessionHealthHabits = {
  non_smoker?: boolean;
  regular_exercise?: boolean;
  healthy_diet?: boolean;
  occasional_alcohol?: boolean;
  no_alcohol?: boolean;
  stress_management?: boolean;
  adequate_sleep?: boolean;
  no_recreational_drugs?: boolean;
  maintains_healthy_weight?: boolean;
  regular_health_checkup?: boolean;
  vegetarian_vegan?: boolean;
  other?: string;
};

type SessionQuickQuestions = {
  pre_existing_medical_condition?: { answer?: string };
  hospitalized_last_2_years?: { answer?: string };
  currently_on_medication?: { answer?: string };
  surgery_last_5_years?: { answer?: string };
  allergies?: { answer?: string };
  tobacco_or_nicotine_use?: { answer?: string };
  family_history_major_illness?: { answer?: string };
  other_medical_condition?: { answer?: string; details?: string };
};

type SessionLifestyleDetails = {
  height_cm?: string | number;
  weight_kg?: string | number;
  bmi?: string | number;
  occupation_type?: string;
  work_profile?: string;
  annual_income_range?: string;
  additional_information?: string;
};

export type ResumeSessionResponse = {
  id?: number | string;
  client_id?: number | string;
  lob_id?: number | string;
  current_step?: number;
  current_step_name?: string;
  step_1_pre_request?: SessionPersonalDetails & {
    country_code?: string;
  };
  step_2_pre_request?: {
    personal_details?: SessionPersonalDetails;
    travel_details?: SessionTravelDetails;
    health_details?: SessionHealthDetails;
    vehicle_details?: SessionVehicleDetails;
  };
  step_3_pre_request?:
    | {
        personal_details?: SessionPersonalDetails;
        driver_details?: SessionDriverDetails;
        vehicle_details?: SessionVehicleDetails;
        health_details?: SessionHealthDetails;
        health_habits?: SessionHealthHabits;
      }
    | unknown;
  step_4_pre_request?: {
    personal_details?: SessionPersonalDetails;
    health_details?: SessionHealthDetails;
    health_habits?: SessionHealthHabits;
    quick_questions?: SessionQuickQuestions;
    medical_conditions?: string[];
    lifestyle_details?: SessionLifestyleDetails;
  };
  step_5_post_request?: unknown;
  step_6_post_request?: {
    additionalPersonalDetails?: Record<string, unknown>;
  } & Record<string, unknown>;
  step_7_post_request?: unknown;
  step_8_post_request?: unknown;
  quote_request?: {
    personal_details?: SessionPersonalDetails;
    travel_details?: SessionTravelDetails;
    health_details?: SessionHealthDetails;
    health_habits?: SessionHealthHabits;
    quick_questions?: SessionQuickQuestions;
    medical_conditions?: string[];
    lifestyle_details?: SessionLifestyleDetails;
    selected_addOns?: unknown[];
    quotes?: unknown[];
  };
  selected_quote?: {
    insurer_name?: string;
    insurer_id?: number | string;
    insurer_logo?: string;
    plan_name?: string;
    plan_id?: number | string;
    premium?: number | string;
    vat?: number | string;
    total_premium?: number | string;
    currency?: string;
    commission?: {
      percentage?: number | string;
      amount?: number | string;
      currency?: string;
    };
    sections?: unknown[];
    selected_addOns?: unknown[];
  } | null;
  raw_request?: {
    personal_details?: SessionPersonalDetails;
    travel_details?: SessionTravelDetails;
    health_details?: SessionHealthDetails;
    health_habits?: SessionHealthHabits;
    quick_questions?: SessionQuickQuestions;
    medical_conditions?: string[];
    lifestyle_details?: SessionLifestyleDetails;
    vehicle?: {
      vehicle_name?: string;
      model_year?: string | number;
      is_modified?: string;
      new_car?: string;
      place_of_regn?: string;
      date_of_regn?: string;
      registration_type?: string;
      premium_amount?: string | number;
    };
    drivers?: {
      nationality?: string;
      dob?: string;
      gender?: string;
      local_license?: string | number;
      other_license?: string | number;
      active_car_policy?: string;
      current_policy_name?: string;
      current_insurer?: string;
      any_claim_last_year?: string;
    };
    variant_details?: {
      VehicleDesc?: string;
    };
  };
  client?: {
    id?: number | string;
    email?: string;
    phone_number?: string;
    first_name?: string;
    last_name?: string;
    country_code?: string;
  };
};

export type ResumeTarget = {
  path: string;
  stepLabel: string;
  stepIndex?: number;
};

export const buildResumeTargetPath = (target: ResumeTarget | any) => {
  return target.path;
};

export type ResumeHydrationState = {
  lobId: LobId;
  target: ResumeTarget;
  sharedPersonalDetails: SharedPersonalDetailsState;
  quoteFlow: {
    sessionData: {
      client: ResumeSessionResponse["client"];
      session_data: ResumeSessionResponse;
    };
    quoteData: ReturnType<typeof buildSelectedQuoteData> | null;
  };
  travel?: {
    personalDetails: TravelPersonalFormValues;
    travelDetails: TravelDetailsFormValues;
    quoteSelection: TravelQuoteSelectionValues;
    documentUpload: TravelDocumentUploadValues;
    additionalInfo: TravelAdditionalInfoValues;
    payment: TravelPaymentValues;
  };
  car?: {
    personalDetails: CarPersonalFormValues;
    vehicleDetails: CarVehicleFormValues;
    driverDetails: CarDriverFormValues;
    quoteSelection: CarQuoteSelectionValues;
    documentUpload: CarDocumentUploadValues;
    additionalInfo: CarAdditionalInfoValues;
    payment: CarPaymentValues;
  };
  health?: {
    personalDetails: HealthPersonalFormValues;
    healthDetails: HealthDetailsFormValues;
    healthHabits: HealthHabitsFormValues;
    quickQuestions: HealthQuickQuestionsFormValues;
    quoteSelection: HealthQuoteSelectionValues;
    documentUpload: HealthDocumentUploadValues;
    additionalInfo: HealthAdditionalInfoValues;
    payment: HealthPaymentValues;
  };
  life?: {
    personalDetails: TravelPersonalFormValues;
    planDetails: LifePlanDetailsFormValues;
    quoteSelection: { selectedQuoteId: string };
    documentUpload: TravelDocumentUploadValues;
    additionalInfo: TravelAdditionalInfoValues;
    payment: TravelPaymentValues;
  };
};

const normalizeCountryCode = (value?: string) =>
  (value || "").replace(/\D/g, "") || LOCALISATION_CONFIG.mobileCountryCode;

const normalizeGender = (value?: string): "male" | "female" | "" => {
  const normalized = (value || "").trim().toLowerCase();
  if (normalized === "m" || normalized === "male") return "male";
  if (normalized === "f" || normalized === "female") return "female";
  return "";
};

const normalizeTravelGender = (value?: string): TravelAdditionalInfoValues["gender"] => {
  const normalized = (value || "").trim().toLowerCase();
  if (normalized === "m" || normalized === "male") return "Male";
  if (normalized === "f" || normalized === "female") return "Female";
  return "";
};

const normalizeHealthGender = (value?: string): HealthAdditionalInfoValues["gender"] => {
  const normalized = (value || "").trim().toLowerCase();
  if (normalized === "m" || normalized === "male") return "Male";
  if (normalized === "f" || normalized === "female") return "Female";
  return "";
};

const normalizeTravelCoverType = (value?: string): TravelDetailsFormValues["coverType"] => {
  const normalized = (value || "").trim().toLowerCase();
  if (normalized === "group") return "group";
  if (normalized === "family") return "family";
  return "individual";
};

const normalizeHealthCoverageType = (value?: string): HealthDetailsFormValues["coverageType"] => {
  const normalized = (value || "").trim().toLowerCase();
  if (normalized === "familyfloater" || normalized === "family_floater") return "family_floater";
  if (normalized === "family" || normalized === "group") return "family";
  return "individual";
};

const normalizeHealthPolicyType = (
  value?: string,
  fallbackFromData = false
): HealthDetailsFormValues["policyType"] => {
  const normalized = (value || "").trim().toLowerCase();

  if (
    normalized === "portingpolicy" ||
    normalized === "porting_policy" ||
    normalized === "porting"
  ) {
    return "porting_policy";
  }

  if (normalized === "newpolicy" || normalized === "new_policy" || normalized === "new") {
    return "newPolicy";
  }

  return fallbackFromData ? "porting_policy" : "newPolicy";
};

const normalizeHealthYesNo = (
  value?: boolean | string
): HealthDetailsFormValues["claimsInLast3Years"] => {
  if (value === true || value === "yes") {
    return "yes";
  }

  if (value === false || value === "no") {
    return "no";
  }

  return "";
};

const normalizeHealthTenure = (
  value?: string | number
): HealthDetailsFormValues["policy_tenure"] | "" => {
  const normalized = String(value ?? "").trim();
  if (
    normalized === "1" ||
    normalized === "2" ||
    normalized === "3" ||
    normalized === "4" ||
    normalized === "5"
  ) {
    return normalized;
  }
  return "";
};

const resolveHealthTenureFromDates = (startDate?: string, endDate?: string) => {
  if (!startDate || !endDate) return "";

  const start = new Date(startDate);
  const end = new Date(endDate);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) {
    return "";
  }

  const dayDifference = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  const years = Math.max(1, Math.ceil(dayDifference / 365));

  return String(Math.min(5, years)) as HealthDetailsFormValues["policy_tenure"];
};

const normalizeSelectedQuoteId = (selectedQuote?: ResumeSessionResponse["selected_quote"]) =>
  String(selectedQuote?.plan_id || selectedQuote?.plan_name || "");

const getStepNumberFromName = (stepName?: string) => {
  const match = (stepName || "").toLowerCase().match(/^step_(\d+)_/);
  return match ? Number(match[1]) : undefined;
};

const normalizeStepName = (stepName?: string) => (stepName || "").trim().toLowerCase();

const resolveConfiguredTarget = (
  sessionData: ResumeSessionResponse,
  targets: Record<string, ResumeTarget>,
  fallbackOrder: readonly string[],
  defaultTarget: ResumeTarget
) => {
  const currentStepName = normalizeStepName(sessionData?.current_step_name);

  if (currentStepName && targets[currentStepName]) {
    return targets[currentStepName];
  }

  if (sessionData?.current_step) {
    const matchedTarget = Object.entries(targets).find(
      ([stepName]) => getStepNumberFromName(stepName) === sessionData?.current_step
    )?.[1];

    if (matchedTarget) return matchedTarget;
  }

  const completedStepName = fallbackOrder.find((stepName) =>
    Boolean((sessionData as any)?.[stepName])
  );
  return completedStepName ? targets[completedStepName] : defaultTarget;
};

const getStepThree = (sessionData: ResumeSessionResponse) =>
  sessionData?.step_3_pre_request as
    | {
        personal_details?: SessionPersonalDetails;
        driver_details?: SessionDriverDetails;
        vehicle_details?: SessionVehicleDetails;
      }
    | undefined;

const resolveLobId = (sessionData: ResumeSessionResponse): LobId => {
  if (Number(sessionData?.lob_id) === LOB_IDS.MOTOR) return LOB_IDS.MOTOR;
  if (Number(sessionData?.lob_id) === LOB_IDS.HEALTH) return LOB_IDS.HEALTH;
  if (Number(sessionData?.lob_id) === LOB_IDS.LIFE) return LOB_IDS.LIFE;
  if (Number(sessionData?.lob_id) === LOB_IDS.TRAVEL) return LOB_IDS.TRAVEL;
  if (
    sessionData?.step_2_pre_request?.health_details ||
    sessionData?.quote_request?.health_details ||
    sessionData?.raw_request?.health_details
  ) {
    return LOB_IDS.HEALTH;
  }
  if (
    sessionData?.step_2_pre_request?.vehicle_details ||
    getStepThree(sessionData)?.driver_details ||
    sessionData?.raw_request?.vehicle ||
    sessionData?.raw_request?.drivers
  ) {
    return LOB_IDS.MOTOR;
  }
  if (
    (sessionData?.step_2_pre_request as Record<string, unknown> | undefined)?.life_insurance ||
    (sessionData?.quote_request as Record<string, unknown> | undefined)?.life_insurance ||
    (sessionData?.raw_request as Record<string, unknown> | undefined)?.life_insurance
  ) {
    return LOB_IDS.LIFE;
  }
  return LOB_IDS.TRAVEL;
};

const normalizeComparable = (value: unknown) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const getSessionQuotesForLogoFallback = (sessionData?: ResumeSessionResponse) => {
  if (!sessionData) return [] as any[];

  const quotes =
    sessionData?.quote_request?.quotes ||
    (sessionData as any)?.responses ||
    (sessionData as any)?.quote?.quote_request?.quotes ||
    (sessionData as any)?.quote?.responses ||
    (sessionData as any)?.raw_request?.quotes ||
    (sessionData as any)?.quotes ||
    [];

  return Array.isArray(quotes) ? quotes.filter(Boolean) : [];
};

const resolveSelectedQuoteInsurerLogo = (
  selectedQuote?: ResumeSessionResponse["selected_quote"],
  sessionData?: ResumeSessionResponse
) => {
  const directLogo = String(
    (selectedQuote as any)?.insurer_logo || (selectedQuote as any)?.insurer?.insurer_logo || ""
  ).trim();

  if (directLogo) return directLogo;

  const selectedInsurerId = normalizeComparable((selectedQuote as any)?.insurer_id);
  const selectedPlanId = normalizeComparable((selectedQuote as any)?.plan_id);
  const selectedInsurerName = normalizeComparable((selectedQuote as any)?.insurer_name);
  const selectedPlanName = normalizeComparable((selectedQuote as any)?.plan_name);
  const sessionQuotes = getSessionQuotesForLogoFallback(sessionData);

  const matchedQuote = sessionQuotes.find((quote: any) => {
    const quoteInsurerId = normalizeComparable(quote?.insurer?.insurer_id ?? quote?.insurer?.id);
    const quotePlanId = normalizeComparable(
      quote?.plan?.id ?? quote?.plan?.plan_id ?? quote?.plan_id ?? quote?.id
    );
    const quoteInsurerName = normalizeComparable(quote?.insurer?.name);
    const quotePlanName = normalizeComparable(quote?.plan?.name);

    if (selectedInsurerId && selectedPlanId) {
      return quoteInsurerId === selectedInsurerId && quotePlanId === selectedPlanId;
    }

    if (selectedInsurerName && selectedPlanName) {
      return quoteInsurerName === selectedInsurerName && quotePlanName === selectedPlanName;
    }

    return false;
  });

  return String(matchedQuote?.insurer?.insurer_logo || "").trim();
};

export const buildSelectedQuoteData = (
  selectedQuote?: ResumeSessionResponse["selected_quote"],
  sessionData?: ResumeSessionResponse
) => {
  if (!selectedQuote) return null;

  const insurerLogo = resolveSelectedQuoteInsurerLogo(selectedQuote, sessionData);

  return {
    insurer: {
      name: selectedQuote.insurer_name,
      insurer_id: selectedQuote.insurer_id,
      id: selectedQuote.insurer_id,
      insurer_logo: insurerLogo,
    },
    plan: {
      id: selectedQuote.plan_id,
      name: selectedQuote.plan_name,
      price: {
        amount: selectedQuote.premium,
        vat: selectedQuote.vat,
        amount_with_vat: selectedQuote.total_premium,
        currency: selectedQuote.currency || LOCALISATION_CONFIG.currencyCode,
      },
      ...(selectedQuote.commission
        ? {
            commission: {
              percentage: selectedQuote.commission.percentage,
              amount: selectedQuote.commission.amount,
              currency: selectedQuote.commission.currency,
            },
          }
        : {}),
    },
    sections: selectedQuote.sections || [],
  };
};

export const buildSharedPersonalDetails = (
  sessionData: ResumeSessionResponse,
  lobId?: LobId
): SharedPersonalDetailsState => {
  const stepOne = sessionData?.step_1_pre_request;
  const stepTwo = sessionData?.step_2_pre_request?.personal_details;
  const stepThree =
    lobId === LOB_IDS.MOTOR ? getStepThree(sessionData)?.personal_details : undefined;
  const quotePersonal = sessionData?.quote_request?.personal_details;
  const rawPersonal = sessionData?.raw_request?.personal_details;
  const client = sessionData?.client;

  return {
    firstName:
      firstNonEmpty(
        stepThree?.first_name,
        stepTwo?.first_name,
        quotePersonal?.first_name,
        rawPersonal?.first_name,
        stepOne?.first_name,
        client?.first_name,
        ""
      ) || "",
    lastName:
      firstNonEmpty(
        stepThree?.last_name,
        stepTwo?.last_name,
        quotePersonal?.last_name,
        rawPersonal?.last_name,
        stepOne?.last_name,
        client?.last_name,
        ""
      ) || "",
    mobileNumber: {
      countryCode: normalizeCountryCode(
        firstNonEmpty(
          stepThree?.mobile_country_code,
          stepTwo?.mobile_country_code,
          quotePersonal?.mobile_country_code,
          rawPersonal?.mobile_country_code,
          stepOne?.country_code,
          client?.country_code,
          LOCALISATION_CONFIG.mobileCountryCode
        )
      ),
      countryCodeIso: "",
      number:
        firstNonEmpty(
          stepThree?.mobile_number,
          stepTwo?.mobile_number,
          quotePersonal?.mobile_number,
          rawPersonal?.mobile_number,
          rawPersonal?.mobile_no,
          stepOne?.mobile_number,
          client?.phone_number,
          ""
        ) || "",
    },
    email:
      firstNonEmpty(
        stepThree?.email,
        stepTwo?.email,
        quotePersonal?.email,
        rawPersonal?.email,
        rawPersonal?.email_id,
        stepOne?.email,
        client?.email,
        ""
      ) || "",
    partnerCode:
      firstNonEmpty(
        stepThree?.partner_code,
        stepTwo?.partner_code,
        quotePersonal?.partner_code,
        rawPersonal?.partner_code,
        stepOne?.partner_code,
        ""
      ) || "",
    friendsAndFamilyContact:
      firstNonEmpty(
        stepThree?.friends_and_family_contact,
        stepTwo?.friends_and_family_contact,
        quotePersonal?.friends_and_family_contact,
        rawPersonal?.friends_and_family_contact,
        stepOne?.friends_and_family_contact,
        ""
      ) || "",
    marketingConsent:
      firstNonEmpty(
        stepThree?.marketing_consent,
        stepTwo?.marketing_consent,
        quotePersonal?.marketing_consent,
        rawPersonal?.marketing_consent,
        stepOne?.marketing_consent,
        "no"
      ) || "no",
  };
};

const buildTravelDetails = (sessionData: ResumeSessionResponse): TravelDetailsFormValues => {
  const stepTravel = sessionData?.step_2_pre_request?.travel_details;
  const quoteTravel = sessionData?.quote_request?.travel_details;
  const rawTravel = sessionData?.raw_request?.travel_details;
  const travellersSource =
    stepTravel?.travellers || quoteTravel?.travellers || rawTravel?.travellers || [];
  const travellers = travellersSource
    .map((traveller) => ({
      name:
        firstNonEmpty(
          traveller.name,
          `${traveller?.first_name || ""} ${traveller?.last_name || ""}`.trim(),
          ""
        ) || "",
      dob: traveller?.date_of_birth || "",
    }))
    .filter((traveller) => traveller.name || traveller.dob);

  return {
    coverage:
      firstNonEmpty(
        stepTravel?.coverage_type,
        quoteTravel?.coverage_type,
        rawTravel?.coverage_type,
        ""
      ) || "",
    coverageLabel:
      firstNonEmpty(
        stepTravel?.coverage_type,
        quoteTravel?.coverage_type,
        rawTravel?.coverage_type,
        ""
      ) || "",
    fromCountry:
      firstNonEmpty(stepTravel?.departure, quoteTravel?.departure, rawTravel?.departure, "") || "",
    destination:
      firstNonEmpty(
        stepTravel?.destination,
        quoteTravel?.destination,
        rawTravel?.destination,
        ""
      ) || "",
    planType:
      firstNonEmpty(
        stepTravel?.plan_type,
        quoteTravel?.plan_type,
        rawTravel?.plan_type,
        "single"
      ) || "single",
    startDate:
      firstNonEmpty(
        stepTravel?.travel_dates?.start_date,
        quoteTravel?.travel_dates?.start_date,
        rawTravel?.travel_dates?.start_date,
        ""
      ) || "",
    endDate:
      firstNonEmpty(
        stepTravel?.travel_dates?.end_date,
        quoteTravel?.travel_dates?.end_date,
        rawTravel?.travel_dates?.end_date,
        ""
      ) || "",
    coverType: normalizeTravelCoverType(
      firstNonEmpty(stepTravel?.cover_type, quoteTravel?.cover_type, rawTravel?.cover_type)
    ),
    travellers: travellers.length ? travellers : [{ name: "", dob: "" }],
  };
};

const buildHealthDetails = (sessionData: ResumeSessionResponse): HealthDetailsFormValues => {
  const stepHealth = sessionData?.step_2_pre_request?.health_details;
  const stepHealthThree = (sessionData?.step_3_pre_request as any)?.health_details;
  const stepHealthFour = sessionData?.step_4_pre_request?.health_details;
  const quoteHealth = sessionData?.quote_request?.health_details;
  const rawHealth = sessionData?.raw_request?.health_details;
  const healthDetails = stepHealth || stepHealthThree || stepHealthFour || quoteHealth || rawHealth;
  const mappedMembers = (healthDetails?.members || []).map((member) => ({
    name:
      firstNonEmpty(
        member?.name,
        `${member?.first_name || ""} ${member?.last_name || ""}`.trim(),
        ""
      ) || "",
    relationship:
      firstNonEmpty(member?.relationship, "") === "spouse" ||
      firstNonEmpty(member?.relationship, "") === "child" ||
      firstNonEmpty(member?.relationship, "") === "parent" ||
      firstNonEmpty(member?.relationship, "") === "self" ||
      firstNonEmpty(member?.relationship, "") === "son" ||
      firstNonEmpty(member?.relationship, "") === "daughter" ||
      firstNonEmpty(member?.relationship, "") === "mother" ||
      firstNonEmpty(member?.relationship, "") === "father"
        ? (member?.relationship as HealthDetailsFormValues["members"][number]["relationship"])
        : "",
    dob: firstNonEmpty(member?.date_of_birth, member?.dob, "") || "",
    preExistingCondition: toBoolean(member?.pre_existing_condition, false),
  }));

  return {
    coverageAmount:
      normalizeAmountValue(
        firstNonEmpty(
          stepHealth?.coverage_amount,
          stepHealthThree?.coverage_amount,
          stepHealthFour?.coverage_amount,
          quoteHealth?.coverage_amount,
          rawHealth?.coverage_amount,
          "500000"
        )
      ) || "500000",
    policyType: normalizeHealthPolicyType(
      firstNonEmpty(
        stepHealth?.policy_type,
        stepHealthThree?.policy_type,
        stepHealthFour?.policy_type,
        quoteHealth?.policy_type,
        rawHealth?.policy_type
      ),
      Boolean(
        firstNonEmpty(
          stepHealth?.current_insurer,
          stepHealth?.existing_policy_details?.current_insurer,
          stepHealthThree?.current_insurer,
          stepHealthThree?.existing_policy_details?.current_insurer,
          stepHealthFour?.current_insurer,
          stepHealthFour?.existing_policy_details?.current_insurer,
          quoteHealth?.current_insurer,
          quoteHealth?.existing_policy_details?.current_insurer,
          rawHealth?.current_insurer
        ) ||
        firstNonEmpty(
          stepHealth?.policy_expiry_date,
          stepHealth?.existing_policy_details?.policy_expiry_date,
          stepHealthThree?.policy_expiry_date,
          stepHealthThree?.existing_policy_details?.policy_expiry_date,
          stepHealthFour?.policy_expiry_date,
          stepHealthFour?.existing_policy_details?.policy_expiry_date,
          quoteHealth?.policy_expiry_date,
          quoteHealth?.existing_policy_details?.policy_expiry_date,
          rawHealth?.policy_expiry_date
        ) ||
        firstNonEmpty(
          stepHealth?.sum_insured,
          stepHealthThree?.sum_insured,
          stepHealthFour?.sum_insured,
          quoteHealth?.sum_insured,
          rawHealth?.sum_insured
        ) ||
        firstNonEmpty(
          stepHealth?.claims_in_last_3_years,
          stepHealthThree?.claims_in_last_3_years,
          stepHealthFour?.claims_in_last_3_years,
          quoteHealth?.claims_in_last_3_years,
          rawHealth?.claims_in_last_3_years
        )
      )
    ),
    coverageType: normalizeHealthCoverageType(
      firstNonEmpty(
        stepHealth?.coverage_type,
        stepHealthThree?.coverage_type,
        stepHealthFour?.coverage_type,
        quoteHealth?.coverage_type,
        rawHealth?.coverage_type
      )
    ),
    currentInsurer:
      firstNonEmpty(
        stepHealth?.current_insurer,
        stepHealth?.existing_policy_details?.current_insurer,
        stepHealthThree?.current_insurer,
        stepHealthThree?.existing_policy_details?.current_insurer,
        stepHealthFour?.current_insurer,
        stepHealthFour?.existing_policy_details?.current_insurer,
        quoteHealth?.current_insurer,
        quoteHealth?.existing_policy_details?.current_insurer,
        rawHealth?.current_insurer,
        ""
      ) || "",
    policyExpiryDate:
      firstNonEmpty(
        stepHealth?.policy_expiry_date,
        stepHealth?.existing_policy_details?.policy_expiry_date,
        stepHealthThree?.policy_expiry_date,
        stepHealthThree?.existing_policy_details?.policy_expiry_date,
        stepHealthFour?.policy_expiry_date,
        stepHealthFour?.existing_policy_details?.policy_expiry_date,
        quoteHealth?.policy_expiry_date,
        quoteHealth?.existing_policy_details?.policy_expiry_date,
        rawHealth?.policy_expiry_date,
        ""
      ) || "",
    sumInsured:
      firstNonEmpty(
        stepHealth?.sum_insured,
        stepHealthThree?.sum_insured,
        stepHealthFour?.sum_insured,
        quoteHealth?.sum_insured,
        rawHealth?.sum_insured,
        ""
      ) || "",
    claimsInLast3Years: normalizeHealthYesNo(
      firstNonEmpty(
        stepHealth?.claims_in_last_3_years,
        stepHealthThree?.claims_in_last_3_years,
        stepHealthFour?.claims_in_last_3_years,
        quoteHealth?.claims_in_last_3_years,
        rawHealth?.claims_in_last_3_years,
        ""
      )
    ),
    startDate:
      firstNonEmpty(
        stepHealth?.start_date,
        stepHealth?.policy_dates?.start_date,
        stepHealthThree?.start_date,
        stepHealthThree?.policy_dates?.start_date,
        stepHealthFour?.start_date,
        stepHealthFour?.policy_dates?.start_date,
        quoteHealth?.start_date,
        quoteHealth?.policy_dates?.start_date,
        rawHealth?.start_date,
        rawHealth?.policy_dates?.start_date,
        ""
      ) || "",
    policy_tenure:
      normalizeHealthTenure(
        firstNonEmpty(
          stepHealth?.policy_tenure,
          stepHealthThree?.policy_tenure,
          stepHealthFour?.policy_tenure,
          quoteHealth?.policy_tenure,
          rawHealth?.policy_tenure
        )
      ) ||
      resolveHealthTenureFromDates(
        firstNonEmpty(
          stepHealth?.start_date,
          stepHealth?.policy_dates?.start_date,
          stepHealthThree?.start_date,
          stepHealthThree?.policy_dates?.start_date,
          stepHealthFour?.start_date,
          stepHealthFour?.policy_dates?.start_date,
          quoteHealth?.start_date,
          quoteHealth?.policy_dates?.start_date,
          rawHealth?.start_date,
          rawHealth?.policy_dates?.start_date,
          ""
        ) || "",
        firstNonEmpty(
          stepHealth?.cover_end_date,
          stepHealth?.policy_dates?.end_date,
          stepHealth?.end_date,
          stepHealthThree?.cover_end_date,
          stepHealthThree?.policy_dates?.end_date,
          stepHealthThree?.end_date,
          stepHealthFour?.cover_end_date,
          stepHealthFour?.policy_dates?.end_date,
          stepHealthFour?.end_date,
          quoteHealth?.cover_end_date,
          quoteHealth?.policy_dates?.end_date,
          quoteHealth?.end_date,
          rawHealth?.cover_end_date,
          rawHealth?.policy_dates?.end_date,
          rawHealth?.end_date,
          ""
        ) || ""
      ) ||
      "1",
    members:
      mappedMembers.length > 0
        ? mappedMembers
        : [
            {
              name: "",
              relationship: "self" as const,
              dob: "",
              preExistingCondition: false,
            },
          ],
  };
};

const normalizeHealthQuestionAnswer = (
  value?: string | boolean
): HealthHabitsFormValues["preExistingMedicalConditions"] => {
  if (value === true || String(value || "").toLowerCase() === "yes") return "yes";
  if (value === false || String(value || "").toLowerCase() === "no") return "no";
  return "";
};

const buildHealthHabits = (sessionData: ResumeSessionResponse): HealthHabitsFormValues => {
  const stepThree = (sessionData?.step_3_pre_request as any)?.health_habits;
  const stepFour = sessionData?.step_4_pre_request?.health_habits;
  const quoteHabits = sessionData?.quote_request?.health_habits;
  const rawHabits = sessionData?.raw_request?.health_habits;
  const habits = stepThree || stepFour || quoteHabits || rawHabits || {};

  const selectedHabits: HealthHabitsFormValues["selectedHabits"] = [];
  if (toBoolean(habits?.non_smoker)) selectedHabits.push("non_smoker");
  if (toBoolean(habits?.regular_exercise)) selectedHabits.push("regular_exercise");
  if (toBoolean(habits?.healthy_diet)) selectedHabits.push("healthy_diet");
  if (toBoolean(habits?.occasional_alcohol)) selectedHabits.push("occasional_alcohol");
  if (toBoolean(habits?.no_alcohol)) selectedHabits.push("no_alcohol");
  if (toBoolean(habits?.stress_management)) selectedHabits.push("stress_management");
  if (toBoolean(habits?.adequate_sleep)) selectedHabits.push("adequate_sleep");
  if (toBoolean(habits?.no_recreational_drugs)) selectedHabits.push("no_recreational_drugs");
  if (toBoolean(habits?.maintains_healthy_weight)) selectedHabits.push("maintains_healthy_weight");
  if (toBoolean(habits?.regular_health_checkup)) selectedHabits.push("regular_health_checkup");
  if (toBoolean(habits?.vegetarian_vegan)) selectedHabits.push("vegetarian_vegan");
  if (firstNonEmpty(habits?.other, "")) selectedHabits.push("other");

  const quickQuestions =
    sessionData?.step_4_pre_request?.quick_questions ||
    sessionData?.quote_request?.quick_questions ||
    sessionData?.raw_request?.quick_questions;

  return {
    selectedHabits,
    preExistingMedicalConditions: normalizeHealthQuestionAnswer(
      quickQuestions?.pre_existing_medical_condition?.answer
    ),
    hospitalizedLast2Years: normalizeHealthQuestionAnswer(
      quickQuestions?.hospitalized_last_2_years?.answer
    ),
    currentlyOnMedication: normalizeHealthQuestionAnswer(
      quickQuestions?.currently_on_medication?.answer
    ),
    surgeryLast5Years: normalizeHealthQuestionAnswer(quickQuestions?.surgery_last_5_years?.answer),
    allergies: normalizeHealthQuestionAnswer(quickQuestions?.allergies?.answer),
    tobaccoOrNicotine: normalizeHealthQuestionAnswer(
      quickQuestions?.tobacco_or_nicotine_use?.answer
    ),
    familyHistoryMajorIllness: normalizeHealthQuestionAnswer(
      quickQuestions?.family_history_major_illness?.answer
    ),
    otherMedicalCondition: normalizeHealthQuestionAnswer(
      quickQuestions?.other_medical_condition?.answer
    ),
  };
};

const buildHealthQuickQuestions = (
  sessionData: ResumeSessionResponse
): HealthQuickQuestionsFormValues => {
  const stepFourLifestyle = sessionData?.step_4_pre_request?.lifestyle_details;
  const quoteLifestyle = sessionData?.quote_request?.lifestyle_details;
  const rawLifestyle = sessionData?.raw_request?.lifestyle_details;
  const lifestyle = stepFourLifestyle || quoteLifestyle || rawLifestyle;

  const quickQuestions =
    sessionData?.step_4_pre_request?.quick_questions ||
    sessionData?.quote_request?.quick_questions ||
    sessionData?.raw_request?.quick_questions;

  const medicalConditions =
    sessionData?.step_4_pre_request?.medical_conditions ||
    sessionData?.quote_request?.medical_conditions ||
    sessionData?.raw_request?.medical_conditions ||
    [];

  return {
    heightCm: toStringValue(lifestyle?.height_cm, ""),
    weightKg: toStringValue(lifestyle?.weight_kg, ""),
    bmi: toStringValue(lifestyle?.bmi, ""),
    medicalConditions: Array.isArray(medicalConditions)
      ? (medicalConditions as HealthQuickQuestionsFormValues["medicalConditions"])
      : [],
    occupationType: toStringValue(
      lifestyle?.occupation_type,
      ""
    ) as HealthQuickQuestionsFormValues["occupationType"],
    workProfile: toStringValue(
      lifestyle?.work_profile,
      ""
    ) as HealthQuickQuestionsFormValues["workProfile"],
    annualIncomeRange: toStringValue(
      lifestyle?.annual_income_range,
      ""
    ) as HealthQuickQuestionsFormValues["annualIncomeRange"],
    additionalInformation: toStringValue(lifestyle?.additional_information, ""),
    otherMedicalConditionDetails: toStringValue(
      quickQuestions?.other_medical_condition?.details,
      ""
    ),
  };
};

const buildCarVehicleDetails = (sessionData: ResumeSessionResponse): CarVehicleFormValues => {
  const stepTwo = sessionData?.step_2_pre_request?.vehicle_details;
  const stepThree = getStepThree(sessionData)?.vehicle_details;
  const rawVehicle = sessionData?.raw_request?.vehicle;

  return {
    carModel:
      firstNonEmpty(stepThree?.car_model, stepTwo?.car_model, rawVehicle?.vehicle_name, "") || "",
    modelYear: String(
      firstNonEmpty(stepThree?.model_year, stepTwo?.model_year, rawVehicle?.model_year, "") || ""
    ),
    vehicleVariant:
      firstNonEmpty(
        stepThree?.vehicle_variant,
        stepTwo?.vehicle_variant,
        sessionData?.raw_request?.variant_details?.VehicleDesc,
        ""
      ) || "",
    gccSpec: toBoolean(firstNonEmpty(stepThree?.gcc_spec, stepTwo?.gcc_spec), false),
    isNewCar: toBoolean(
      firstNonEmpty(stepThree?.is_new_car, stepTwo?.is_new_car, rawVehicle?.new_car),
      false
    ),
    registeredEmirate:
      firstNonEmpty(
        stepThree?.registered_emirate,
        stepTwo?.registered_emirate,
        rawVehicle?.place_of_regn,
        ""
      ) || "",
    registrationDate:
      firstNonEmpty(
        stepThree?.registration_date,
        stepTwo?.registration_date,
        rawVehicle?.date_of_regn,
        ""
      ) || "",
    registeredUnder:
      firstNonEmpty(
        stepThree?.registered_under,
        stepTwo?.registered_under,
        rawVehicle?.registration_type
      ) === "Company"
        ? "Company"
        : "Individual",
    carValue: String(
      firstNonEmpty(stepThree?.car_value, stepTwo?.car_value, rawVehicle?.premium_amount, "") || ""
    ),
  };
};

const buildCarDriverDetails = (sessionData: ResumeSessionResponse): CarDriverFormValues => {
  const stepDriver = getStepThree(sessionData)?.driver_details;
  const rawDrivers = sessionData.raw_request?.drivers;

  return {
    dateOfBirth: firstNonEmpty(stepDriver?.date_of_birth, rawDrivers?.dob, "") || "",
    nationality: firstNonEmpty(stepDriver?.nationality, rawDrivers?.nationality, "") || "",
    gender: normalizeGender(firstNonEmpty(stepDriver?.gender, rawDrivers?.gender)),
    uaeDrivingExperience: String(
      firstNonEmpty(stepDriver?.uae_driving_experience, rawDrivers?.local_license, "") || ""
    ),
    outsideUaeDrivingExperience: String(
      firstNonEmpty(stepDriver?.outside_uae_driving_experience, rawDrivers?.other_license, "") || ""
    ),
    hasActivePolicy: toBoolean(
      firstNonEmpty(stepDriver?.has_active_policy, rawDrivers?.active_car_policy),
      false
    ),
    currentPolicyType:
      firstNonEmpty(stepDriver?.current_policy_type, rawDrivers?.current_policy_name, "") || "",
    currentInsurer:
      firstNonEmpty(stepDriver?.current_insurer, rawDrivers?.current_insurer, "") || "",
    claimsLastYear: toBoolean(
      firstNonEmpty(stepDriver?.claims_last_year, rawDrivers?.any_claim_last_year),
      false
    ),
    no_claims_certificate: stepDriver?.no_claims_certificate || "",
  };
};

const collectUploadedDocumentKeys = (stepData: unknown) => {
  const payload =
    readRecordValue(stepData, "attachments", "documents", "documentUpload") || stepData;

  if (Array.isArray(payload)) {
    return payload
      .map((document) => readRecordValue(document, "fieldKey", "field_key", "key", "name"))
      .filter(Boolean)
      .map(String);
  }

  if (!isRecord(payload)) return [];

  return Object.entries(payload)
    .filter(([, value]) => Boolean(value))
    .map(([key, value]) => {
      const nestedKey = readRecordValue(value, "fieldKey", "field_key", "key", "name");
      return String(nestedKey || key);
    });
};

const withCombinedDocumentFlags = <TValues extends Record<string, boolean>>(
  uploadedDocuments: TValues,
  pairs: Array<[string, string]>
) => {
  const nextDocuments: Record<string, boolean> = { ...uploadedDocuments };

  pairs.forEach(([primaryKey, secondaryKey]) => {
    if (nextDocuments[primaryKey] === true && nextDocuments[secondaryKey] !== true) {
      nextDocuments[secondaryKey] = true;
    }
  });

  return nextDocuments as TValues;
};

const withTravelCombinedDocumentFlags = (uploadedDocuments: Record<string, boolean>) => {
  const pairs: Array<[string, string]> = [
    ["passport", "passportBack"],
    ["emiratesIdFront", "emiratesIdBack"],
  ];

  Object.keys(uploadedDocuments).forEach((key) => {
    const travellerPassportMatch = key.match(/^traveller_(\d+)_passport$/);
    const travellerEmiratesMatch = key.match(/^traveller_(\d+)_emiratesIdFront$/);

    if (travellerPassportMatch) {
      pairs.push([key, `traveller_${travellerPassportMatch[1]}_passportBack`]);
    }

    if (travellerEmiratesMatch) {
      pairs.push([key, `traveller_${travellerEmiratesMatch[1]}_emiratesIdBack`]);
    }
  });

  return withCombinedDocumentFlags(uploadedDocuments, pairs);
};

const buildTravelDocumentUpload = (
  sessionData: ResumeSessionResponse
): TravelDocumentUploadValues => {
  const documentKeys = collectUploadedDocumentKeys(sessionData?.step_5_post_request);
  const uploadedDocuments = documentKeys.reduce<Record<string, boolean>>((documents, key) => {
    documents[key] = true;
    return documents;
  }, {});
  const combinedUploadedDocuments = withTravelCombinedDocumentFlags(uploadedDocuments);

  return {
    passport: false,
    passportBack: false,
    passportCombined: false,
    visaCopy: false,
    confirmDocuments: documentKeys.length > 0,
    emiratesIdFront: false,
    emiratesIdBack: false,
    emiratesIdCombined: false,
    ...combinedUploadedDocuments,
  };
};

const buildLifeDocumentUpload = (
  sessionData: ResumeSessionResponse
): TravelDocumentUploadValues => {
  // Life's doc-upload save advances the session to step_6, so read from step_6 first,
  // falling back to step_5 for older sessions saved before that change.
  const documentKeys = collectUploadedDocumentKeys(
    sessionData?.step_5_post_request || sessionData?.step_6_post_request
  );
  const uploadedDocuments = documentKeys.reduce<Record<string, boolean>>((documents, key) => {
    documents[key] = true;
    return documents;
  }, {});

  return {
    passport: false,
    passportBack: false,
    passportCombined: false,
    visaCopy: false,
    confirmDocuments: documentKeys.length > 0,
    emiratesIdFront: false,
    emiratesIdBack: false,
    emiratesIdCombined: false,
    ...uploadedDocuments,
  };
};

const buildCarDocumentUpload = (sessionData: ResumeSessionResponse): CarDocumentUploadValues => {
  const documentKeys = collectUploadedDocumentKeys(sessionData.step_5_post_request);
  const uploadedDocuments = documentKeys.reduce<Partial<CarDocumentUploadValues>>(
    (documents, key) => {
      if (key !== "confirmDocuments") {
        documents[key as keyof CarDocumentUploadValues] = true;
      }
      return documents;
    },
    {}
  );
  const combinedUploadedDocuments = withCombinedDocumentFlags(uploadedDocuments, [
    ["emiratesIdFront", "emiratesIdBack"],
    ["drivingLicenseFront", "drivingLicenseBack"],
  ]);

  return {
    emiratesIdFront: false,
    emiratesIdBack: false,
    emiratesIdCombined: false,
    drivingLicenseFront: false,
    drivingLicenseBack: false,
    drivingLicenseCombined: false,
    confirmDocuments: documentKeys.length > 0,
    vehicleLicense: false,
    noClaimProof: false,
    otherDocument: false,
    ...combinedUploadedDocuments,
  };
};

const buildHealthDocumentUpload = (
  sessionData: ResumeSessionResponse
): HealthDocumentUploadValues => {
  const documentKeys = collectUploadedDocumentKeys(sessionData.step_5_post_request);
  const uploadedDocuments = documentKeys.reduce<Partial<HealthDocumentUploadValues>>(
    (documents, key) => {
      if (key !== "confirmDocuments") {
        documents[key] = true;
      }
      return documents;
    },
    {}
  );

  return {
    previousPolicy: false,
    healthInsuranceCard: false,
    medicalReport: false,
    confirmDocuments: documentKeys.length > 0,
    ...uploadedDocuments,
  };
};

const getAdditionalInfoPayload = (sessionData: ResumeSessionResponse) =>
  readRecordValue(
    sessionData?.step_6_post_request,
    "additionalPersonalDetails",
    "additional_personal_details",
    "additionalInfo",
    "additional_info"
  ) || sessionData?.step_6_post_request;

const buildTravelAdditionalInfo = (
  sessionData: ResumeSessionResponse
): TravelAdditionalInfoValues => {
  const payload = getAdditionalInfoPayload(sessionData);

  return {
    gender: normalizeTravelGender(toStringValue(readRecordValue(payload, "gender"))),
    nationality: toStringValue(readRecordValue(payload, "nationality")),
    cityOfResidence: toStringValue(
      readRecordValue(payload, "cityOfResidence", "city_of_residence", "city")
    ),
    address: toStringValue(readRecordValue(payload, "address")),
    emiratesId: toStringValue(readRecordValue(payload, "emiratesId", "emirates_id")),
    passportNumber: toStringValue(readRecordValue(payload, "passportNumber", "passport_number")),
    sourceOfFund: toStringValue(readRecordValue(payload, "sourceOfFund", "source_of_fund")),
    beneficiaryName: toStringValue(readRecordValue(payload, "beneficiaryName", "beneficiary_name")),
    beneficiaryRelation:
      readRecordValue(payload, "beneficiaryRelation", "beneficiary_relationship") === "Other"
        ? "Other"
        : readRecordValue(payload, "beneficiaryRelation", "beneficiary_relationship") ===
            "Legal Heir"
          ? "Legal Heir"
          : "",
    uaeResidentConfirm: toBoolean(
      readRecordValue(payload, "uaeResidentConfirm", "uae_resident_confirm"),
      false
    ),
    travellers: Array.isArray(readRecordValue(payload, "travellers"))
      ? (readRecordValue(payload, "travellers") as TravelAdditionalInfoValues["travellers"])
      : [],
  };
};

const buildHealthAdditionalInfo = (
  sessionData: ResumeSessionResponse
): HealthAdditionalInfoValues => {
  const payload = getAdditionalInfoPayload(sessionData);

  return {
    gender: normalizeHealthGender(toStringValue(readRecordValue(payload, "gender"))),
    nationality: toStringValue(readRecordValue(payload, "nationality")),
    cityOfResidence: toStringValue(
      readRecordValue(payload, "cityOfResidence", "city_of_residence", "city")
    ),
    address: toStringValue(readRecordValue(payload, "address")),
    emiratesId: toStringValue(readRecordValue(payload, "emiratesId", "emirates_id")),
    passportNumber: toStringValue(readRecordValue(payload, "passportNumber", "passport_number")),
    sourceOfFund: toStringValue(readRecordValue(payload, "sourceOfFund", "source_of_fund")),
    beneficiaryName: toStringValue(readRecordValue(payload, "beneficiaryName", "beneficiary_name")),
    beneficiaryRelation:
      readRecordValue(payload, "beneficiaryRelation", "beneficiary_relationship") === "Other"
        ? "Other"
        : readRecordValue(payload, "beneficiaryRelation", "beneficiary_relationship") ===
            "Legal Heir"
          ? "Legal Heir"
          : "",
    uaeResidentConfirm: toBoolean(
      readRecordValue(payload, "uaeResidentConfirm", "uae_resident_confirm"),
      false
    ),
  };
};

const buildCarAdditionalInfo = (sessionData: ResumeSessionResponse): CarAdditionalInfoValues => {
  const payload = getAdditionalInfoPayload(sessionData);

  return {
    estimatedAnnualMileage: toStringValue(
      readRecordValue(payload, "estimatedAnnualMileage", "estimated_annual_mileage")
    ),
    chassisNumber: toStringValue(readRecordValue(payload, "chassisNumber", "chassis_number")),
    emiratesId: toStringValue(readRecordValue(payload, "emiratesId", "emirates_id")),
    carFinancedBy: toStringValue(readRecordValue(payload, "carFinancedBy", "car_financed_by")),
    vehicleColor: toStringValue(readRecordValue(payload, "vehicleColor", "vehicle_color")),
    tcfNumber: toStringValue(readRecordValue(payload, "tcfNumber", "tcf_number")),
    registrationType: toStringValue(
      readRecordValue(payload, "registrationType", "registration_type")
    ),
    plateCode: toStringValue(readRecordValue(payload, "plateCode", "plate_code")),
    plateNumber: toStringValue(readRecordValue(payload, "plateNumber", "plate_number")),
    engineNumber: toStringValue(readRecordValue(payload, "engineNumber", "engine_number")),
    authorizationAccepted: toBoolean(
      readRecordValue(payload, "authorizationAccepted", "authorization_accepted"),
      false
    ),
  };
};

const getPaymentPayload = (sessionData: ResumeSessionResponse) =>
  readRecordValue(
    sessionData?.step_7_post_request,
    "payment",
    "paymentDetails",
    "payment_details"
  ) || sessionData?.step_7_post_request;

const buildPaymentValues = (
  sessionData: ResumeSessionResponse,
  sharedPersonalDetails: SharedPersonalDetailsState
): TravelPaymentValues & CarPaymentValues & HealthPaymentValues => {
  const payload = getPaymentPayload(sessionData);
  const billingEmail = toStringValue(
    readRecordValue(payload, "billingEmail", "billing_email", "email"),
    sharedPersonalDetails.email
  );

  return {
    paymentMethod: toStringValue(
      readRecordValue(payload, "paymentMethod", "payment_method"),
      "Card"
    ),
    billingEmail,
    cardHolder: toStringValue(readRecordValue(payload, "cardHolder", "card_holder")),
    cardNumber: toStringValue(readRecordValue(payload, "cardNumber", "card_number")),
    expiry: toStringValue(readRecordValue(payload, "expiry", "expiry_date")),
    cvv: toStringValue(readRecordValue(payload, "cvv")),
    confirmPayment: toBoolean(readRecordValue(payload, "confirmPayment", "confirm_payment"), false),
  };
};

const getTravelTarget = (sessionData: ResumeSessionResponse): ResumeTarget => {
  return resolveConfiguredTarget(
    sessionData,
    TRAVEL_RESUME_STEP_TARGETS,
    TRAVEL_RESUME_STEP_FALLBACK_ORDER,
    TRAVEL_RESUME_STEP_TARGETS.step_1_pre_request
  );
};

const getCarTarget = (sessionData: ResumeSessionResponse): ResumeTarget => {
  return resolveConfiguredTarget(
    sessionData,
    MOTOR_RESUME_STEP_TARGETS,
    MOTOR_RESUME_STEP_FALLBACK_ORDER,
    MOTOR_RESUME_STEP_TARGETS?.step_1_pre_request
  );
};

const getHealthTarget = (sessionData: ResumeSessionResponse): ResumeTarget => {
  return resolveConfiguredTarget(
    sessionData,
    HEALTH_RESUME_STEP_TARGETS,
    HEALTH_RESUME_STEP_FALLBACK_ORDER,
    HEALTH_RESUME_STEP_TARGETS?.step_1_pre_request
  );
};

const getLifeTarget = (sessionData: ResumeSessionResponse): ResumeTarget => {
  return resolveConfiguredTarget(
    sessionData,
    LIFE_RESUME_STEP_TARGETS,
    LIFE_RESUME_STEP_FALLBACK_ORDER,
    LIFE_RESUME_STEP_TARGETS?.step_1_pre_request
  );
};

export const hydrateResumeJourneyState = (
  sessionData: ResumeSessionResponse
): ResumeHydrationState => {
  const lobId = resolveLobId(sessionData);
  const sharedPersonalDetails = buildSharedPersonalDetails(sessionData, lobId);
  const selectedQuoteId = normalizeSelectedQuoteId(sessionData?.selected_quote);
  const quoteData = buildSelectedQuoteData(sessionData?.selected_quote, sessionData);
  const quoteFlow = {
    sessionData: {
      client: sessionData?.client,
      session_data: sessionData,
    },
    quoteData,
  };

  if (lobId === LOB_IDS?.MOTOR) {
    return {
      lobId,
      target: getCarTarget(sessionData),
      sharedPersonalDetails,
      quoteFlow,
      car: {
        personalDetails: sharedPersonalDetails,
        vehicleDetails: buildCarVehicleDetails(sessionData),
        driverDetails: buildCarDriverDetails(sessionData),
        quoteSelection: {
          selectedQuoteId,
        },
        documentUpload: buildCarDocumentUpload(sessionData),
        additionalInfo: buildCarAdditionalInfo(sessionData),
        payment: buildPaymentValues(sessionData, sharedPersonalDetails),
      },
    };
  }

  if (lobId === LOB_IDS?.HEALTH) {
    return {
      lobId,
      target: getHealthTarget(sessionData),
      sharedPersonalDetails,
      quoteFlow,
      health: {
        personalDetails: sharedPersonalDetails,
        healthDetails: buildHealthDetails(sessionData),
        healthHabits: buildHealthHabits(sessionData),
        quickQuestions: buildHealthQuickQuestions(sessionData),
        quoteSelection: {
          selectedQuoteId,
        },
        documentUpload: buildHealthDocumentUpload(sessionData),
        additionalInfo: buildHealthAdditionalInfo(sessionData),
        payment: buildPaymentValues(sessionData, sharedPersonalDetails),
      },
    };
  }

  if (lobId === LOB_IDS?.LIFE) {
    const lifeInsurance = ((sessionData?.step_2_pre_request as Record<string, unknown> | undefined)
      ?.life_insurance ||
      (sessionData?.quote_request as Record<string, unknown> | undefined)?.life_insurance ||
      (sessionData?.raw_request as Record<string, unknown> | undefined)?.life_insurance ||
      {}) as Record<string, unknown>;
    return {
      lobId,
      target: getLifeTarget(sessionData),
      sharedPersonalDetails,
      quoteFlow,
      life: {
        personalDetails: sharedPersonalDetails,
        planDetails: {
          lifeAssuredDob: toStringValue(lifeInsurance.life_assured_date_of_birth, ""),
          lifeAssuredGender: normalizeGender(toStringValue(lifeInsurance.life_assured_gender, "")),
          smoker: toStringValue(lifeInsurance.smoker, "") as "yes" | "no" | "",
          planOption: toStringValue(lifeInsurance.plan_option, ""),
          sumAssured: toStringValue(lifeInsurance.sum_assured, ""),
          policyTerm: toStringValue(lifeInsurance.policy_term, ""),
          premiumPaymentTerm: toStringValue(lifeInsurance.premium_payment_term, ""),
          premiumPaymentFrequency: toStringValue(lifeInsurance.premium_payment_frequency, ""),
          category: toStringValue(lifeInsurance.category, ""),
          payoutType: toStringValue(lifeInsurance.payout_type, ""),
          payoutTerm: toStringValue(lifeInsurance.payout_term, ""),
          lumpsumPercentage: toStringValue(lifeInsurance.lumpsum_percentage, ""),
          accidentalDeathSumAssured: toStringValue(lifeInsurance.accidental_death_sum_assured, ""),
        },
        quoteSelection: { selectedQuoteId },
        documentUpload: buildLifeDocumentUpload(sessionData),
        additionalInfo: buildTravelAdditionalInfo(sessionData),
        payment: buildPaymentValues(sessionData, sharedPersonalDetails),
      },
    };
  }

  return {
    lobId,
    target: getTravelTarget(sessionData),
    sharedPersonalDetails,
    quoteFlow,
    travel: {
      personalDetails: sharedPersonalDetails,
      travelDetails: buildTravelDetails(sessionData),
      quoteSelection: {
        selectedQuoteId,
      },
      documentUpload: buildTravelDocumentUpload(sessionData),
      additionalInfo: buildTravelAdditionalInfo(sessionData),
      payment: buildPaymentValues(sessionData, sharedPersonalDetails),
    },
  };
};

export const getClientIdFromVerifyResponse = (
  verifyResponseData:
    | {
        client_id?: string | number;
        client?: { id?: string | number };
      }
    | null
    | undefined,
  fallbackClientId?: string | number | null
) => {
  const candidate =
    verifyResponseData?.client_id || verifyResponseData?.client?.id || fallbackClientId || "";

  return String(candidate).trim();
};
